import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import api from "../../api";
import "./WheelOfFortuneModal.css";

// Item image mapping for wheel segments
const itemImages = {
  "Ruby": "/items/ruby.png",
  "Sapphire": "/items/sapphire.png",
  "Emerald": "/items/emerald.png",
  "Gold": "/items/gold.png",
  "Diamond": "/items/diamond.png",
  "Rock": "/items/rock.png",
  "Fire Stone": "/items/fire_stone.png",
  "Water Stone": "/items/water_stone.png",
  "Thunder Stone": "/items/thunder_stone.png",
  "Leaf Stone": "/items/leaf_stone.png",
  "Moon Stone": "/items/moon_stone.png",
  "Super Lure": "/items/super_lure.png",
  "Mining Potion": "/items/mining_potion.png",
  "Potion": "/items/potion.png",
  "Super Potion": "/items/super_potion.png",
  "Safe": "/items/safe.png",
  "Ruby Gummy": "/items/ruby_gummy.png",
  "Sapphire Gummy": "/items/sapphire_gummy.png",
  "Emerald Gummy": "/items/emerald_gummy.png",
  "Golden Gummy": "/items/golden_gummy.png",
  "Diamond Gummy": "/items/diamond_gummy.png"
};

const WheelOfFortuneModal = ({ isOpen, toggle, playerPos, spawnText }) => {
  const [userCurrency, setUserCurrency] = useState({ coins: 0, money: 0 });
  const [isSpinning, setIsSpinning] = useState(false);
  const [spinResult, setSpinResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [wheelRotation, setWheelRotation] = useState(0);
  const [showResult, setShowResult] = useState(false);

  // Wheel segments configuration (matches backend probabilities)
  const wheelSegments = [
    { name: "Ruby", color: "#dc2626", category: "common", image: itemImages["Ruby"] || "/items/ruby.png" },
    { name: "Super Lure", color: "#7c3aed", category: "uncommon", image: itemImages["Super Lure"] || "/items/super_lure.png" },
    { name: "Sapphire", color: "#2563eb", category: "common", image: itemImages["Sapphire"] || "/items/sapphire.png" },
    { name: "Mining Potion", color: "#7c3aed", category: "uncommon", image: itemImages["Mining Potion"] || "/items/mining_potion.png" },
    { name: "Emerald", color: "#16a34a", category: "common", image: itemImages["Emerald"] || "/items/emerald.png" },
    { name: "Ruby Gummy", color: "#f59e0b", category: "rare", image: itemImages["Ruby Gummy"] || "/items/ruby_gummy.png" },
    { name: "Gold", color: "#eab308", category: "common", image: itemImages["Gold"] || "/items/gold.png" },
    { name: "Potion", color: "#7c3aed", category: "uncommon", image: itemImages["Potion"] || "/items/potion.png" },
    { name: "Rock", color: "#6b7280", category: "common", image: itemImages["Rock"] || "/items/rock.png" },
    { name: "Safe", color: "#7c3aed", category: "uncommon", image: itemImages["Safe"] || "/items/safe.png" },
    { name: "Fire Stone", color: "#dc2626", category: "common", image: itemImages["Fire Stone"] || "/items/fire_stone.png" },
    { name: "Diamond Gummy", color: "#f59e0b", category: "rare", image: itemImages["Diamond Gummy"] || "/items/diamond_gummy.png" },
  ];

  useEffect(() => {
    if (isOpen) {
      fetchUserCurrency();
    }
  }, [isOpen]);

  const fetchUserCurrency = async () => {
    try {
      const response = await api.get("/v1/wheeloffortune/currency", { withCredentials: true });
      setUserCurrency({
        coins: response.data.coins,
        money: response.data.money
      });
    } catch (err) {
      console.error("Failed to fetch user currency:", err);
      setError("Failed to load currency balance");
    } finally {
      setLoading(false);
    }
  };

  const handleSpin = async () => {
    if (userCurrency.coins < 500) {
      setError("Not enough coins! You need 500 coins to spin.");
      return;
    }

    setIsSpinning(true);
    setError("");
    setShowResult(false);

    try {
      // Calculate random rotation (multiple full spins + random position)
      const spins = 5 + Math.random() * 5; // 5-10 full rotations for smoother experience
      const finalRotation = wheelRotation + (spins * 360) + Math.random() * 360;
      setWheelRotation(finalRotation);

      // Call backend API
      const response = await api.post("/v1/wheeloffortune/spin", {}, { withCredentials: true });
      
      // Update coins
      setUserCurrency(prev => ({
        ...prev,
        coins: prev.coins - 500
      }));
      
      // Show result after wheel animation
      setTimeout(() => {
        setSpinResult(response.data.reward);
        setShowResult(true);
        setIsSpinning(false);
        
        // Spawn floating text if available
        if (spawnText && playerPos) {
          // Show item gained with green text
          spawnText(
            `+${response.data.reward.quantity}x ${response.data.reward.name}!`,
            playerPos.x,
            playerPos.y - 20,
            response.data.reward.category === "rare" ? "#f59e0b" : 
            response.data.reward.category === "uncommon" ? "#7c3aed" : "#16a34a"
          );
          
          // Show coins spent with red text
          setTimeout(() => {
            spawnText(
              `-500 🪙`,
              playerPos.x,
              playerPos.y,
              "#ef4444"
            );
          }, 500);
        }
      }, 4000); // Wait for wheel animation

    } catch (err) {
      console.error("Spin failed:", err);
      setError(err.response?.data?.detail || "Failed to spin the wheel");
      setIsSpinning(false);
    }
  };

  const closeResult = () => {
    setShowResult(false);
    // Refresh user currency after closing result
    fetchUserCurrency();
    setSpinResult(null);
  };

  if (!isOpen) return null;

  return (
    <div className="wheel-of-fortune-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Wheel of Fortune</h2>
          <button className="close-button" onClick={toggle}>×</button>
        </div>
        
        <div className="modal-body">
          {loading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
              <span>Loading...</span>
            </div>
          ) : (
            <>
              <div className="wheel-container">
                <div className="wheel-pointer"></div>
                <motion.div 
                  className="wheel"
                  animate={{ rotate: wheelRotation }}
                  transition={{ 
                    duration: isSpinning ? 4 : 0, 
                    ease: isSpinning ? [0.2, 0.1, 0.3, 1.0] : "linear",
                    type: "spring",
                    stiffness: isSpinning ? 10 : 100,
                    damping: isSpinning ? 50 : 10
                  }}
                >
                  {wheelSegments.map((segment, index) => (
                    <div
                      key={index}
                      className={`wheel-segment ${segment.category}`}
                      style={{
                        transform: `rotate(${(360 / wheelSegments.length) * index}deg)`,
                        backgroundColor: segment.color,
                      }}
                    >
                      <div className="segment-content">
                        <img 
                          src={segment.image} 
                          alt={segment.name} 
                          className="segment-image"
                        />
                        <span className="segment-name">{segment.name}</span>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>
              
              <div className="controls">
                <div className="currency-display">
                  <div className="coin-display">
                    <img src="/items/coin.png" alt="Coins" className="currency-icon" />
                    <span>{userCurrency.coins.toLocaleString()} coins</span>
                  </div>
                  <div className="money-display">
                    <img src="/items/money.png" alt="Money" className="currency-icon" />
                    <span>${userCurrency.money.toLocaleString()}</span>
                  </div>
                </div>
                
                <button 
                  className={`spin-button ${isSpinning ? "spinning" : ""}`}
                  onClick={handleSpin}
                  disabled={isSpinning || userCurrency.coins < 500}
                >
                  {isSpinning ? (
                    <>
                      <div className="spin-loader"></div>
                      <span>Spinning...</span>
                    </>
                  ) : (
                    <>
                      <img src="/items/coin.png" alt="Coins" className="button-coin-icon" />
                      <span>Spin (500 coins)</span>
                    </>
                  )}
                </button>
                
                {error && <div className="error-message">{error}</div>}
              </div>
              
              <div className="probability-info">
                <h3>Probabilities:</h3>
                <div className="probability-categories">
                  <div className="probability-category common">
                    <span className="category-name">Common</span>
                    <span className="category-chance">80%</span>
                  </div>
                  <div className="probability-category uncommon">
                    <span className="category-name">Uncommon</span>
                    <span className="category-chance">19%</span>
                  </div>
                  <div className="probability-category rare">
                    <span className="category-name">Rare</span>
                    <span className="category-chance">1%</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {showResult && spinResult && (
        <motion.div 
          className="result-modal"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="result-header">
            <h3> Congratulations!</h3>
            <div className={`result-category-badge ${spinResult.category}`}>
              {spinResult.category}
            </div>
          </div>
          <div className={`result-item ${spinResult.category}`}>
            <div className="result-item-image-container">
              <div className="result-item-glow"></div>
              <img 
                src={itemImages[spinResult.name] || `/items/${spinResult.name.toLowerCase().replace(/ /g, '_')}.png`}
                alt={spinResult.name}
                className="result-item-image"
              />
            </div>
            <div className="result-item-details">
              <h4 className="item-name">{spinResult.name}</h4>
              <p className="item-quantity">x{spinResult.quantity}</p>
            </div>
          </div>
          <button className="close-result" onClick={closeResult}>
            Collect Reward
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default WheelOfFortuneModal;
